// PromotionsList.js
import { useState, useEffect } from "react";
import ListControls from "../ListControls/ListControls";
import Pagination from "../Pagination/pagination";
import {
  getPromotionsList,
  createPromotion,
  updatePromotion,
  deletePromotion,
  getPromotionDetails,
  setPromotionStatus,
} from "../../service/Mainservice";
import Loader from "../../Loader/Loader";
import Modal from "../Model/Model";
import "./Promotions.css";

// MUI Icons
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ToggleOnIcon from "@mui/icons-material/ToggleOn";
import ToggleOffIcon from "@mui/icons-material/ToggleOff";

import Tooltip from "@mui/material/Tooltip";
import InsightsIcon from "@mui/icons-material/Insights";

const PromotionsList = () => {
  const [loading, setLoading] = useState(true);
  const [promotions, setPromotions] = useState([]);
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("desc");
  const [pagination, setPagination] = useState({ page: 1, limit: 10, total: 0 });
  const [modalOpen, setModalOpen] = useState(false);
  const [editingPromotion, setEditingPromotion] = useState(null);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [promotionDetails, setPromotionDetails] = useState(null);
  const [statusUpdatingId, setStatusUpdatingId] = useState(null);
  const [formData, setFormData] = useState({
    code: "",
    discountType: "percentage",
    discountValue: "",
    maxDiscount: "",
    minOrderValue: "",
    startDate: "",
    expiryDate: "",
    usageLimit: "",
    vendorId: "",
    firstTimeOnly: false,
    targetUserIds: [],
    isActive: true,
  });

  const fetchPromotions = async (page = 1) => {
    setLoading(true);
    try {
      const res = await getPromotionsList({ status: "all", page, pageSize: 10, sortBy, sortOrder });
      setPromotions(res.data || []);
      setPagination(res.pagination || { page, limit: 10, total: 0 });
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPromotions();
  }, [sortBy, sortOrder]);

  const openCreateModal = () => {
    setEditingPromotion(null);
    setFormData({
      code: "",
      discountType: "percentage",
      discountValue: "",
      maxDiscount: "",
      minOrderValue: "",
      startDate: "",
      expiryDate: "",
      usageLimit: "",
      vendorId: "",
      firstTimeOnly: false,
      targetUserIds: [],
      isActive: true,
    });
    setModalOpen(true);
  };

  const openEditModal = (promo) => {
    setEditingPromotion(promo);
    setFormData({
      code: promo.code,
      discountType: promo.discountType || "percentage",
      discountValue: promo.discountValue,
      maxDiscount: promo.maxDiscount || "",
      minOrderValue: promo.minOrderValue || "",
      startDate: promo.startDate ? promo.startDate.split("T")[0] : "",
      expiryDate: promo.expiryDate ? promo.expiryDate.split("T")[0] : "",
      usageLimit: promo.usageLimit || "",
      vendorId: promo.vendorId || "",
      firstTimeOnly: promo.firstTimeOnly || false,
      targetUserIds: promo.targetUserIds || [],
      isActive: promo.isActive,
    });
    setModalOpen(true);
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const payload = {
        ...formData,
        discountValue: parseFloat(formData.discountValue),
        maxDiscount: formData.maxDiscount
          ? parseFloat(formData.maxDiscount)
          : undefined,
        minOrderValue: formData.minOrderValue
          ? parseFloat(formData.minOrderValue)
          : undefined,
        usageLimit: formData.usageLimit
          ? parseInt(formData.usageLimit)
          : undefined,
        vendorId: formData.vendorId || undefined,
        targetUserIds: formData.targetUserIds.length
          ? formData.targetUserIds
          : undefined,
      };
      if (editingPromotion) {
        await updatePromotion({ id: editingPromotion.id, ...payload });
      } else {
        await createPromotion(payload);
      }
      setModalOpen(false);
      fetchPromotions();
    } catch (err) {
      alert(err.response?.data?.message || "Operation failed.");
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (id, action) => {
    if (!action) return;
    if (!window.confirm(`Are you sure you want to ${action} this promotion?`)) return;
    setStatusUpdatingId(id);
    try {
      await setPromotionStatus(id, action);
      fetchPromotions();
    } catch (err) {
      alert(err.data?.message || err.response?.data?.message || "Status update failed.");
    } finally {
      setStatusUpdatingId(null);
    }
  };

  const openDetailsModal = async (id) => {
    setDetailsModalOpen(true);
    setDetailsLoading(true);
    setPromotionDetails(null);
    try {
      const res = await getPromotionDetails(id);
      setPromotionDetails(res.data || null);
    } catch (err) {
      alert(err.data?.message || err.response?.data?.message || "Unable to load promotion details.");
      setDetailsModalOpen(false);
    } finally {
      setDetailsLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this promotion?"))
      return;
    try {
      await deletePromotion({ id });
      fetchPromotions();
    } catch (err) {
      alert(err.response?.data?.message || "Delete failed.");
    }
  };

  const formatCurrency = (val) => `$${parseFloat(val || 0).toFixed(2)}`;

  return (
    <div className="promotions-page">
      <ListControls sortOptions={[{value:"createdAt",label:"Created date"},{value:"code",label:"Code"},{value:"discountValue",label:"Discount"},{value:"usageCount",label:"Usage"},{value:"expiryDate",label:"Expiry date"}]} sortBy={sortBy} sortOrder={sortOrder} onSortByChange={setSortBy} onSortOrderChange={setSortOrder} />
      <div className="page-actions">
        <button className="btn-primary" onClick={openCreateModal}>
          <AddIcon /> New Promotion
        </button>
      </div>

      {loading ? (
        <Loader />
      ) : (
        <div className="table-wrapper">
          <table className="data_table">
            <thead>
              <tr>
                <th>Code</th>
                <th>Type</th>
                <th>Value</th>
                <th>Min Order</th>
                <th>Start</th>
                <th>Expiry</th>
                <th>Usage</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {promotions.length === 0 ? (
                <tr>
                  <td colSpan="9" style={{ textAlign: "center" }}>
                    No promotions found.
                  </td>
                </tr>
              ) : (
                promotions.map((p) => (
                  <tr key={p.id}>
                    <td>
                      <strong>{p.code}</strong>
                    </td>
                    <td>{p.discountType || "flat"}</td>
                    <td>
                      {p.discountType === "percentage"
                        ? `${p.discountValue}%`
                        : formatCurrency(p.discountValue)}
                    </td>
                    <td>
                      {p.minOrderValue ? formatCurrency(p.minOrderValue) : "-"}
                    </td>
                    <td>
                      {p.startDate
                        ? new Date(p.startDate).toLocaleDateString()
                        : "-"}
                    </td>
                    <td>
                      {p.expiryDate
                        ? new Date(p.expiryDate).toLocaleDateString()
                        : "-"}
                    </td>
                    <td>
                      {p.usageCount || 0} / {p.usageLimit || "∞"}
                    </td>
                    <td>
                      <span
                        className={`status-badge status-${p.status || (p.isActive ? "active" : "inactive")}`}
                      >
                        {p.status || (p.isActive ? "Active" : "Inactive")}
                      </span>
                    </td>
                    <td>
                      <div className="action_buttons">
                        <Tooltip title="Details & analytics" arrow>
                          <InsightsIcon
                            sx={{ color: "#0f766e", fontSize: 25, cursor: "pointer" }}
                            onClick={() => openDetailsModal(p.id)}
                          />
                        </Tooltip>
                        <Tooltip title="Edit" arrow>
                          <EditIcon
                            sx={{
                              color: "#3b82f6",
                              fontSize: 25,
                              cursor: "pointer",
                              transition: "0.2s",
                              "&:hover": {
                                color: "#2563eb",
                                transform: "scale(1.1)",
                              },
                            }}
                            onClick={() => openEditModal(p)}
                          />
                        </Tooltip>

                        <select
                          className="promotion-status-action"
                          value=""
                          disabled={statusUpdatingId === p.id}
                          onChange={(e) => handleStatusChange(p.id, e.target.value)}
                          aria-label={`Change status for ${p.code}`}
                        >
                          <option value="">{statusUpdatingId === p.id ? "Updating..." : "Status action"}</option>
                          <option value="pause">Pause</option>
                          <option value="resume">Resume</option>
                          <option value="expire">Expire</option>
                          <option value="disable">Disable</option>
                        </select>

                        <Tooltip title="Delete" arrow>
                          <DeleteIcon
                            sx={{
                              color: "var(--red)",
                              fontSize: 28,
                              cursor: "pointer",
                              transition: "0.2s",
                              "&:hover": {
                                color: "#dc2626",
                                transform: "scale(1.1)",
                              },
                            }}
                            onClick={() => handleDelete(p.id)}
                          />
                        </Tooltip>
                      </div>{" "}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
          <Pagination pagedata={pagination} onPageChange={fetchPromotions} />
        </div>
      )}

      <Modal
        isOpen={detailsModalOpen}
        onClose={() => setDetailsModalOpen(false)}
        title="Promotion Details & Analytics"
        maxWidth="900px"
      >
        {detailsLoading ? (
          <Loader />
        ) : promotionDetails ? (
          <div className="promotion-details">
            <div className="promotion-details__header">
              <div>
                <span className="promotion-details__eyebrow">Promotion</span>
                <h3>{promotionDetails.promotion?.title || promotionDetails.promotion?.code || "Promotion"}</h3>
                <p>{promotionDetails.promotion?.description || "No description provided."}</p>
              </div>
              <span className={`status-badge status-${promotionDetails.promotion?.status || "inactive"}`}>
                {promotionDetails.promotion?.status || "Unknown"}
              </span>
            </div>

            <div className="analytics-grid">
              <div className="analytics-card"><span>Redemptions</span><strong>{promotionDetails.analytics?.usageCount || 0}</strong></div>
              <div className="analytics-card"><span>Unique Customers</span><strong>{promotionDetails.analytics?.uniqueCustomers || 0}</strong></div>
              <div className="analytics-card"><span>Sales Volume</span><strong>{formatCurrency(promotionDetails.analytics?.totalSalesVolume)}</strong></div>
              <div className="analytics-card"><span>Total Discount</span><strong>{formatCurrency(promotionDetails.analytics?.totalDiscountGiven)}</strong></div>
              <div className="analytics-card"><span>Admin Cost</span><strong>{formatCurrency(promotionDetails.analytics?.adminDiscountCost)}</strong></div>
              <div className="analytics-card"><span>Vendor Cost</span><strong>{formatCurrency(promotionDetails.analytics?.vendorDiscountCost)}</strong></div>
            </div>

            <h4 className="participating-stores-title">Participating Stores</h4>
            <div className="table-wrapper">
              <table className="data_table analytics-stores-table">
                <thead><tr><th>Store</th><th>Redemptions</th><th>Discount</th><th>Sales</th></tr></thead>
                <tbody>
                  {(promotionDetails.analytics?.participatingStores || []).length ? (
                    promotionDetails.analytics.participatingStores.map((store) => (
                      <tr key={store.vendorId}>
                        <td>{store.shopName || store.vendorId}</td>
                        <td>{store.count || 0}</td>
                        <td>{formatCurrency(store.totalDiscount)}</td>
                        <td>{formatCurrency(store.totalSales)}</td>
                      </tr>
                    ))
                  ) : (
                    <tr><td colSpan="4" className="analytics-empty">No participating-store data.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        ) : null}
      </Modal>

      {/* Create/Edit Modal */}
      <Modal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editingPromotion ? "Edit Promotion" : "Create Promotion"}
      >
        <div className="promotion-form">
          <div className="form-row">
            <div className="form-group">
              <label>Code *</label>
              <input
                type="text"
                value={formData.code}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    code: e.target.value.toUpperCase(),
                  })
                }
                placeholder="e.g. SUMMER25"
              />
            </div>
            <div className="form-group">
              <label>Discount Type *</label>
              <select
                value={formData.discountType}
                onChange={(e) =>
                  setFormData({ ...formData, discountType: e.target.value })
                }
              >
                <option value="percentage">Percentage</option>
                <option value="flat">Flat Amount</option>
                <option value="free_delivery">Free Delivery</option>
              </select>
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Discount Value *</label>
              <input
                type="number"
                value={formData.discountValue}
                onChange={(e) =>
                  setFormData({ ...formData, discountValue: e.target.value })
                }
                placeholder="e.g. 10"
                step="0.01"
              />
            </div>
            <div className="form-group">
              <label>Max Discount (optional)</label>
              <input
                type="number"
                value={formData.maxDiscount}
                onChange={(e) =>
                  setFormData({ ...formData, maxDiscount: e.target.value })
                }
                placeholder="e.g. 50"
                step="0.01"
              />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Minimum Order Value</label>
              <input
                type="number"
                value={formData.minOrderValue}
                onChange={(e) =>
                  setFormData({ ...formData, minOrderValue: e.target.value })
                }
                placeholder="e.g. 20"
                step="0.01"
              />
            </div>
            <div className="form-group">
              <label>Usage Limit</label>
              <input
                type="number"
                value={formData.usageLimit}
                onChange={(e) =>
                  setFormData({ ...formData, usageLimit: e.target.value })
                }
                placeholder="e.g. 100"
              />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Start Date</label>
              <input
                type="date"
                value={formData.startDate}
                onChange={(e) =>
                  setFormData({ ...formData, startDate: e.target.value })
                }
              />
            </div>
            <div className="form-group">
              <label>Expiry Date</label>
              <input
                type="date"
                value={formData.expiryDate}
                onChange={(e) =>
                  setFormData({ ...formData, expiryDate: e.target.value })
                }
              />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Vendor ID (optional)</label>
              <input
                type="text"
                value={formData.vendorId}
                onChange={(e) =>
                  setFormData({ ...formData, vendorId: e.target.value })
                }
                placeholder="Store ID"
              />
            </div>
            <div className="form-group checkbox-group">
              <label>
                <input
                  type="checkbox"
                  checked={formData.firstTimeOnly}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      firstTimeOnly: e.target.checked,
                    })
                  }
                />
                First-time customers only
              </label>
            </div>
          </div>
          <div className="form-group">
            <label>Target User IDs (comma separated)</label>
            <input
              type="text"
              value={formData.targetUserIds.join(",")}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  targetUserIds: e.target.value
                    ? e.target.value.split(",").map(Number)
                    : [],
                })
              }
              placeholder="e.g. 1,2,3"
            />
          </div>
          <div className="modal-actions">
            <button
              className="btn-secondary"
              onClick={() => setModalOpen(false)}
            >
              Cancel
            </button>
            <button
              className="btn-primary"
              onClick={handleSubmit}
              disabled={loading}
            >
              {loading ? "Saving..." : editingPromotion ? "Update" : "Create"}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default PromotionsList;
